import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const editUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  is_active: z.boolean(),
  agency_id: z.string().nullable(),
});

type EditUserFormData = z.infer<typeof editUserSchema>;

interface Agency {
  id: string;
  name: string;
  code: string;
}

interface UserToEdit {
  id: string;
  user_id: string;
  email: string;
  is_active: boolean;
  agency_id: string | null;
  agency_name: string | null;
  roles: string[];
}

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserToEdit | null;
  onUserUpdated: () => void;
  currentUserId?: string;
}

export function EditUserDialog({
  open,
  onOpenChange,
  user,
  onUserUpdated,
  currentUserId,
}: EditUserDialogProps) {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      email: '',
      is_active: true,
      agency_id: null,
    },
  });

  // Load agencies when dialog opens
  useEffect(() => {
    if (open) {
      loadAgencies();
    }
  }, [open]);

  // Reset form when user changes
  useEffect(() => {
    if (user) {
      form.reset({
        email: user.email,
        is_active: user.is_active,
        agency_id: user.agency_id,
      });
    }
  }, [user, form]);

  const loadAgencies = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, code')
        .order('name');

      if (error) throw error;
      setAgencies(data || []);
    } catch (error) {
      console.error('Error loading agencies:', error);
      toast.error('Failed to load agencies');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: EditUserFormData) => {
    if (!user) return;

    setIsSaving(true);
    try {
      // Update the profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          email: data.email,
          is_active: data.is_active,
          agency_id: data.agency_id,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.user_id);

      if (profileError) throw profileError;

      // Log the audit event
      await supabase.from('admin_audit_logs').insert({
        actor_user_id: currentUserId,
        action: 'user_updated',
        target_resource: 'profiles',
        target_id: user.user_id,
        metadata: {
          email: user.email,
          changes: {
            email: data.email !== user.email ? { from: user.email, to: data.email } : undefined,
            is_active: data.is_active !== user.is_active ? { from: user.is_active, to: data.is_active } : undefined,
            agency_id: data.agency_id !== user.agency_id ? { from: user.agency_id, to: data.agency_id } : undefined,
          },
        },
      });

      toast.success('User updated successfully');
      onUserUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  const isSelf = user?.user_id === currentUserId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user details and permissions. Changes will take effect immediately.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="user@example.com"
                        {...field}
                        disabled={isSaving}
                      />
                    </FormControl>
                    <FormDescription>
                      The user's email address for login and notifications.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="agency_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agency</FormLabel>
                    <Select
                      value={field.value || 'none'}
                      onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                      disabled={isSaving}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an agency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No Agency</SelectItem>
                        {agencies.map((agency) => (
                          <SelectItem key={agency.id} value={agency.id}>
                            {agency.name} ({agency.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The agency this user belongs to.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active Status</FormLabel>
                      <FormDescription>
                        {isSelf
                          ? 'You cannot deactivate your own account.'
                          : 'Inactive users cannot log in or access the system.'}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isSaving || isSelf}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {user && user.roles.length > 0 && (
                <div className="rounded-lg border p-4 bg-muted/50">
                  <p className="text-sm font-medium mb-2">Current Roles</p>
                  <div className="flex flex-wrap gap-2">
                    {user.roles.map((role) => (
                      <span
                        key={role}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                      >
                        {role.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Use the Manage Roles dropdown in the users table to modify roles.
                  </p>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
